import { getNumberFromQueryString, getStringArrayFromQueryString, getStringFromQueryString } from '@if/core/utils'
import DescriptionIcon from '@mui/icons-material/Description'
import EditIcon from '@mui/icons-material/Edit'
import LinkIcon from '@mui/icons-material/Link'
import { IconButton, Link, Tooltip } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { EExceptionStatus } from '@src/tools/error-tracker/types/Constants'
import { ExceptionLog } from '@src/tools/error-tracker/types/ExceptionLog'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@src/shell/utils/Formatter'
import { getStartDate } from './browseExceptions-selectors'

export function useExceptionsColumns(
  handleRowClick: (params: GridRenderCellParams<ExceptionLog>) => void,
  handleAddTicket?: (params: GridRenderCellParams<ExceptionLog>) => void,
  shouldShowTicketColumn = true,
) {
  const { t } = useTranslation()

  const columns = useMemo<GridColDef<ExceptionLog>[]>(() => {
    const baseColumns = [
      {
        field: 'id',
        flex: 0.25,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'applicationName',
        headerName: t('exceptionsColumns.applicationName'),
        description: t('exceptionsColumns.applicationName'),
        flex: 1.5,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'operationName',
        headerName: t('exceptionsColumns.operationName'),
        description: t('exceptionsColumns.operationName'),
        flex: 1.25,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'message',
        headerName: t('exceptionsColumns.message'),
        description: t('exceptionsColumns.message'),
        flex: 3,
        headerAlign: 'left',
        align: 'left',
        display: 'flex',
      },
      {
        field: 'count',
        headerName: t('exceptionsColumns.count'),
        description: t('exceptionsColumns.count'),
        flex: 0.5,
        headerAlign: 'left',
        align: 'left',
        display: 'flex',
      },
      {
        field: 'firstTimeGenerated',
        headerName: t('exceptionsColumns.date'),
        description: t('exceptionsColumns.date'),
        flex: 1.75,
        headerAlign: 'left',
        align: 'left',
        display: 'flex',
        alignItems: 'center',
        renderCell: (params) => (
          <div style={{ textAlign: 'left' }}>
            {params.row.lastDetectedOn && (
              <p style={{ margin: '0', fontSize: 'smaller' }}>
                {t('exceptionsColumns.lastDetectedOn')}: {formatDateTime(new Date(params.row.lastDetectedOn))}
              </p>
            )}
            {params.row.firstTimeGenerated && (
              <p style={{ margin: '0', fontSize: 'smaller', opacity: 0.6 }}>
                {t('exceptionsColumns.createdOn')}: {formatDateTime(new Date(params.row.firstTimeGenerated))}
              </p>
            )}
          </div>
        ),
      },
    ] as GridColDef<ExceptionLog>[]

    if (shouldShowTicketColumn) {
      baseColumns.push({
        field: 'devopsTicketNumber',
        headerName: t('exceptionsColumns.currentTicket'),
        description: t('exceptionsColumns.currentTicket'),
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        display: 'flex',
        renderCell: (params) => {
          const DevopsBaseUrl = 'https://dev.azure.com/IcebergFinance/IcebergFinance/_workitems/edit/'
          const hasTickets = params.row.devopsTicketNumbers && params.row.devopsTicketNumbers.length > 0
          const currentTicket = params.row.devopsTicketNumber
          const ticketCount = hasTickets ? params.row.devopsTicketNumbers.length : 0

          return (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div style={{ flexGrow: 1 }}>
                {currentTicket ? (
                  <div>
                    <Link href={DevopsBaseUrl + currentTicket} target="_blank" style={{ textDecoration: 'none' }}>
                      #{currentTicket}
                    </Link>
                    {ticketCount > 1 && (
                      <span style={{ fontSize: 'smaller', opacity: 0.6, marginLeft: '4px' }}>
                        (+{ticketCount - 1} {ticketCount > 2 ? 'others' : 'other'})
                      </span>
                    )}
                  </div>
                ) : (
                  <span style={{ opacity: 0.6, fontStyle: 'italic' }}>No ticket</span>
                )}
              </div>
            </div>
          )
        },
      })
    }

    baseColumns.push({
      field: 'actions',
      headerName: t('exceptionsColumns.actions'),
      description: t('exceptionsColumns.actions'),
      flex: 0.75,
      minWidth: 140,
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
      editable: true,
      renderCell: (params) => {
        return (
          <>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {handleAddTicket && (
                <Tooltip title={t('exceptionsColumns.addNewTicket')}>
                  <IconButton onClick={() => handleAddTicket(params)}>
                    <DescriptionIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('common.edit')}>
                <IconButton onClick={() => handleRowClick(params)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {params.row.hyperlink && (
                <Tooltip title={t('exceptionsColumns.openInAppInsights')}>
                  <Link href={params.row.hyperlink} target="_blank">
                    <LinkIcon style={{ marginTop: 8 }} />
                  </Link>
                </Tooltip>
              )}
            </div>
          </>
        )
      },
    })

    return baseColumns
  }, [handleRowClick, handleAddTicket, t, shouldShowTicketColumn])
  return columns
}

export function useSearchQuery(searchParams: URLSearchParams) {
  const query = useMemo(() => {
    return {
      applicationName: getStringFromQueryString(searchParams, 'applicationName', ''),
      startDate: getStringFromQueryString(searchParams, 'startDate', getStartDate()),
      statuses: getStringArrayFromQueryString(searchParams, 'statuses', [EExceptionStatus.New]),
      sortBy: getStringFromQueryString(searchParams, 'sortBy', 'LastDetectedOn'),
      limit: getNumberFromQueryString(searchParams, 'limit', 25),
      offset: getNumberFromQueryString(searchParams, 'offset', 0),
    }
  }, [searchParams])
  return query
}
